import { EyeIcon, EyeOffIcon } from '@heroicons/react/outline';
import { Icon, Input, InputProps } from '@vechaiui/react';
import { useField } from 'formik';
import { useToggle } from '@react-hookz/web';

import Field from '#/components/forms/Field';
import { isRequiredYupField, useYupRequiredFields } from '#/components/forms/Formik';

export const InputFormik: React.FC<InputFormikProps> = ({
  children: _children,
  formatter,
  invalid,
  label,
  leftAddon,
  name,
  rightAddon,
  ...props
}) => {
  const [field, meta, helper] = useField(name);
  const requiredFields = useYupRequiredFields();
  const isRequired = isRequiredYupField(requiredFields, name);
  const showError = meta.touched && !!meta.error;
  const [isPasswordVisible, togglePasswordVisibility] = useToggle(false);

  const handleBlur = (ev: React.FocusEvent<HTMLInputElement>) => {
    if (formatter) {
      helper.setValue(formatter(field.value));
    }
    field.onBlur(ev);
  };
  const handleChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    if (ev.target.files) {
      helper.setValue(ev.target.files.length === 1 ? ev.target.files[0] : ev.target.files);
    } else {
      field.onChange(ev);
    }
  };

  return (
    <Field name={name}>
      {label && (
        <label className="uppercase mb-1 inline-block" htmlFor={`input-${name}`}>
          {label} {isRequired && <span className="text-red-600">*</span>}
        </label>
      )}
      <Input.Group size={props.size ?? 'lg'}>
        {leftAddon && <Input.LeftAddon>{leftAddon}</Input.LeftAddon>}
        <Input
          {...field}
          {...props}
          id={`input-${name}`}
          invalid={invalid ?? showError}
          onBlur={handleBlur}
          onChange={handleChange}
          required={props.required ?? isRequired}
          type={isPasswordVisible ? 'text' : props.type}
          value={props.type === 'file' ? undefined : field.value}
        />
        {props.type === 'password' && (
          <Input.RightElement className="w-12" onClick={togglePasswordVisibility}>
            <Icon
              as={!isPasswordVisible ? EyeIcon : EyeOffIcon}
              label="Show Password"
              className="w-4 text-neutral-400"
            />
          </Input.RightElement>
        )}
        {rightAddon && <Input.RightAddon>{rightAddon}</Input.RightAddon>}
      </Input.Group>
    </Field>
  );
};
export type InputFormikProps = InputProps & {
  formatter?: (value: string) => string;
  label?: string;
  leftAddon?: React.ReactNode;
  name: string;
  rightAddon?: React.ReactNode;
};
